import React, { Fragment } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { InfoOutlined, Send } from '@material-ui/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Teacher } from '../../../../interfaces/examiner/Teacher';
import { Exam } from '../../../../interfaces/examiner/Exam';
import { useStyles } from './styles';
import { RecipientRow, SchoolGroup, Selection } from './types';
import { buildRecipientRows, isHtmlBodyEmpty } from './helpers';
import { CopyEmailsButton } from './CopyEmailsButton';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const RECIPIENTS_INFO_TOOLTIP = (
  <Fragment>
    <Typography variant="subtitle2" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
      Who appears in the lists
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 8 }}>
      <strong>Teachers</strong> tab - all teachers with status <em>Active</em>, loaded from the
      teachers database.
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 8 }}>
      <strong>Exams</strong> tab - all exams (sorted by title), each showing how many teachers are linked
      to that exam.
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 8 }}>
      <strong>Schools</strong> tab - all schools from active teachers, each showing how many teachers
      belong to that school.
    </Typography>
    <Typography variant="subtitle2" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
      Who receives the email
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 4 }}>
      • <strong>Individual teacher</strong> - one message to that teacher's email address.
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 4 }}>
      • <strong>Exam</strong> - adds each teacher assigned to that exam. You can remove individual
      teachers on the Teachers tab.
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12, marginBottom: 4 }}>
      • <strong>School</strong> - adds every active teacher at that school. You can remove individual
      teachers on the Teachers tab.
    </Typography>
    <Typography component="p" variant="body2" style={{ fontSize: 12 }}>
      Each teacher receives at most one email, even if included through multiple selections. The
      recipient count and confirmation dialog show the deduplicated list before sending.
    </Typography>
  </Fragment>
);

export interface ComposePanelProps {
  selections: Selection[];
  exams: Exam[];
  schoolGroups: SchoolGroup[];
  teacherById: Map<string, Teacher>;
  totalRecipients: number;
  sending: boolean;
  onRemoveSelection: (sel: Selection) => void;
  onClearSelections: () => void;
  onSend: (subject: string, body: string) => Promise<boolean>;
}

export function ComposePanel({
  selections,
  exams,
  schoolGroups,
  teacherById,
  totalRecipients,
  sending,
  onRemoveSelection,
  onClearSelections,
  onSend,
}: ComposePanelProps) {
  const classes = useStyles();
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [emailsCopiedOpen, setEmailsCopiedOpen] = React.useState(false);
  const [copyAnimating, setCopyAnimating] = React.useState(false);
  const copyAnimTimeoutRef = React.useRef<number | null>(null);

  const recipientRows = React.useMemo<RecipientRow[]>(
    () => buildRecipientRows(selections, exams, schoolGroups, teacherById),
    [selections, exams, schoolGroups, teacherById]
  );

  const teacherSelections = React.useMemo(
    () => selections.filter((sel) => sel.type === 'teacher'),
    [selections]
  );

  const canSend =
    teacherSelections.length > 0 && !!subject.trim() && !isHtmlBodyEmpty(body) && !sending;

  const handleOpenConfirm = () => {
    if (canSend) setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (!sending) setConfirmOpen(false);
  };

  const handleConfirmSend = async () => {
    const cleared = await onSend(subject, body);
    setConfirmOpen(false);
    if (cleared) {
      setSubject('');
      setBody('');
    }
  };

  React.useEffect(() => {
    return () => {
      if (copyAnimTimeoutRef.current !== null) {
        window.clearTimeout(copyAnimTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyRecipientEmails = async () => {
    const emails = Array.from(
      new Set(
        recipientRows
          .map((row) => row.email.trim())
          .filter((email) => email && email !== '-')
      )
    );
    if (!emails.length) return;
    try {
      await navigator.clipboard.writeText(emails.join('\n'));
      setCopyAnimating(true);
      setEmailsCopiedOpen(true);
      if (copyAnimTimeoutRef.current !== null) {
        window.clearTimeout(copyAnimTimeoutRef.current);
      }
      copyAnimTimeoutRef.current = window.setTimeout(() => {
        setCopyAnimating(false);
        copyAnimTimeoutRef.current = null;
      }, 650);
    } catch {
      // clipboard unavailable
    }
  };

  const hasCopyableEmails = recipientRows.some(
    (row) => row.email.trim() && row.email.trim() !== '-'
  );

  return (
    <Paper className={classes.rightPanel} variant="outlined">
      <Box className={classes.composeHeader}>
        <Typography variant="subtitle2" color="textSecondary" className={classes.composeHeaderTitle}>
          Compose email (Email Template: <i>One Time Send</i>)
        </Typography>
        <Tooltip
          arrow
          interactive
          placement="bottom-end"
          leaveDelay={200}
          title={<Box className={classes.recipientsInfoTooltip}>{RECIPIENTS_INFO_TOOLTIP}</Box>}
        >
          <IconButton
            size="small"
            className={classes.recipientsInfoButton}
            aria-label="How recipients are selected"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* To row */}
      <Box
        className={`${classes.toRow} ${teacherSelections.length === 0 ? classes.toRowEmpty : ''}`}
      >
        <Typography
          className={`${classes.toLabel} ${teacherSelections.length === 0 ? classes.toLabelEmpty : ''}`}
        >
          To:
        </Typography>
        {teacherSelections.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, paddingTop: 1 }}>
            Select Exams, Schools or Teachers from the left panel
          </Typography>
        ) : (
          <Fragment>
            {teacherSelections.map((sel) => {
              const email = teacherById.get(sel.id)?.email?.trim();
              const chip = (
                <Chip
                  label={sel.label}
                  size="small"
                  classes={{ root: `${classes.chip} ${classes.chipTeacher}` }}
                  onDelete={() => onRemoveSelection(sel)}
                />
              );

              return (
                <Tooltip
                  key={`${sel.type}-${sel.id}`}
                  arrow
                  placement="top"
                  title={email || 'No email address'}
                >
                  <span>{chip}</span>
                </Tooltip>
              );
            })}
            <Button
              className={classes.clearAllButton}
              color="secondary"
              disabled={sending}
              onClick={onClearSelections}
            >
              Clear all
            </Button>
          </Fragment>
        )}
      </Box>

      {/* Subject row */}
      <Box className={classes.subjectRow}>
        <Typography className={classes.subjectLabel} style={{ paddingTop: 10, paddingBottom: 10 }}>
          Subject:
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter subject…"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          InputProps={{ disableUnderline: true, style: { fontSize: 14 } }}
        />
      </Box>

      {/* Body */}
      <Box className={classes.bodyArea}>
        <ReactQuill
          className={classes.quillEditor}
          theme="snow"
          value={body}
          onChange={setBody}
          modules={quillModules}
          placeholder="Write your message here…"
        />
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Typography className={classes.recipientCount}>
          {totalRecipients > 0
            ? `Sending to ${totalRecipients} teacher${totalRecipients !== 1 ? 's' : ''}`
            : 'No recipients selected'}
        </Typography>
        <Button variant="contained" color="primary" disabled={!canSend} onClick={handleOpenConfirm}>
          Check before sending
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="md"
        fullWidth
        aria-labelledby="send-email-confirm-title"
      >
        <DialogTitle id="send-email-confirm-title">
          Confirm recipients ({recipientRows.length})
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Review who will receive this email before sending.
          </Typography>
          <TableContainer className={classes.confirmTable}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Teacher name</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Exam title</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      Teacher email
                      <Tooltip title="Copy all emails">
                        <span>
                          <CopyEmailsButton
                            animating={copyAnimating}
                            ariaLabel="Copy all recipient emails"
                            disabled={!hasCopyableEmails}
                            onClick={() => void handleCopyRecipientEmails()}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipientRows.map((row) => (
                  <TableRow key={row.teacherId}>
                    <TableCell>{row.teacherName}</TableCell>
                    <TableCell>{row.school}</TableCell>
                    <TableCell>{row.examTitles.length ? row.examTitles.join(', ') : '-'}</TableCell>
                    <TableCell>{row.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="secondary" disabled={sending}>
            Edit Email
          </Button>
          <Button
            onClick={handleConfirmSend}
            color="primary"
            variant="contained"
            disabled={sending || recipientRows.length === 0}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send />}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={emailsCopiedOpen}
        autoHideDuration={3000}
        onClose={() => setEmailsCopiedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={classes.snackbarSuccess}>Email addresses copied to clipboard</Box>
      </Snackbar>
    </Paper>
  );
}
